/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CadastralAuthorization } from '../models/cadastral-authorization';
import { CadastralUserSettings } from '../models/cadastral-user-settings';
import { Commission } from '../models/commission';
import { CostHitAllowed } from '../models/cost-hit-allowed';
import { DiscontinueLenderRate } from '../models/discontinue-lender-rate';
import { DocumentSyncRunResult } from '../models/document-sync-run-result';
import { DocumentSyncSettings } from '../models/document-sync-settings';
import { JsonPatchDocument } from '../models/json-patch-document';
import { LenderChange } from '../models/lender-change';
import { LenderOverview } from '../models/lender-overview';
import { NewLender } from '../models/new-lender';
import { NewLenderRate } from '../models/new-lender-rate';
import { NewInterestSheet } from '../models/new-interest-sheet';
import { NewRateVersion } from '../models/new-rate-version';
import { Organization } from '../models/organization';
import { RateConfigurationIssue } from '../models/rate-configuration-issue';
import { RateExport } from '../models/rate-export';
import { RateImportResult } from '../models/rate-import-result';
import { RatePostcode } from '../models/rate-postcode';
import { RateProfile } from '../models/rate-profile';
import { RateProfileLtvClass } from '../models/rate-profile-ltv-class';
import { RateProfilePermissions } from '../models/rate-profile-permissions';
import { RateRegionList } from '../models/rate-region-list';
import { RateSettings } from '../models/rate-settings';
import { RateInterestSheet } from '../models/rate-interest-sheet';
import { RateInterestSheetProfile } from '../models/rate-interest-sheet-profile';
import { RateVersion } from '../models/rate-version';
import { User } from '../models/user';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getOrganizationById
   */
  static readonly GetOrganizationByIdPath = '/api/v1/Organizations/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOrganizationById$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOrganizationById$Plain$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Organization>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetOrganizationByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Organization>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOrganizationById$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOrganizationById$Plain(params: {
    id: number;
  },
  context?: HttpContext

): Observable<Organization> {

    return this.getOrganizationById$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Organization>) => r.body as Organization)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOrganizationById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOrganizationById$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Organization>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetOrganizationByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Organization>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOrganizationById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOrganizationById(params: {
    id: number;
  },
  context?: HttpContext

): Observable<Organization> {

    return this.getOrganizationById$Response(params,context).pipe(
      map((r: StrictHttpResponse<Organization>) => r.body as Organization)
    );
  }

  /**
   * Path part for operation updateOrganization
   */
  static readonly UpdateOrganizationPath = '/api/v1/Organizations/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateOrganization$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateOrganization$Plain$Response(params: {
    id: number;
    body?: Organization
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Organization>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateOrganizationPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Organization>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateOrganization$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateOrganization$Plain(params: {
    id: number;
    body?: Organization
  },
  context?: HttpContext

): Observable<Organization> {

    return this.updateOrganization$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Organization>) => r.body as Organization)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateOrganization()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateOrganization$Response(params: {
    id: number;
    body?: Organization
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Organization>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateOrganizationPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Organization>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateOrganization$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateOrganization(params: {
    id: number;
    body?: Organization
  },
  context?: HttpContext

): Observable<Organization> {

    return this.updateOrganization$Response(params,context).pipe(
      map((r: StrictHttpResponse<Organization>) => r.body as Organization)
    );
  }

  /**
   * Path part for operation patchOrganization
   */
  static readonly PatchOrganizationPath = '/api/v1/Organizations/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `patchOrganization$Plain()` instead.
   *
   * This method sends `application/json-patch+json` and handles request body of type `application/json-patch+json`.
   */
  patchOrganization$Plain$Response(params: {
    id: number;
    body?: JsonPatchDocument
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Organization>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.PatchOrganizationPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json-patch+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Organization>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `patchOrganization$Plain$Response()` instead.
   *
   * This method sends `application/json-patch+json` and handles request body of type `application/json-patch+json`.
   */
  patchOrganization$Plain(params: {
    id: number;
    body?: JsonPatchDocument
  },
  context?: HttpContext

): Observable<Organization> {

    return this.patchOrganization$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Organization>) => r.body as Organization)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `patchOrganization()` instead.
   *
   * This method sends `application/json-patch+json` and handles request body of type `application/json-patch+json`.
   */
  patchOrganization$Response(params: {
    id: number;
    body?: JsonPatchDocument
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Organization>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.PatchOrganizationPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json-patch+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Organization>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `patchOrganization$Response()` instead.
   *
   * This method sends `application/json-patch+json` and handles request body of type `application/json-patch+json`.
   */
  patchOrganization(params: {
    id: number;
    body?: JsonPatchDocument
  },
  context?: HttpContext

): Observable<Organization> {

    return this.patchOrganization$Response(params,context).pipe(
      map((r: StrictHttpResponse<Organization>) => r.body as Organization)
    );
  }

  /**
   * Path part for operation getOrganizationLogo
   */
  static readonly GetOrganizationLogoPath = '/api/v1/Organizations/{id}/Logo';

  /**
   * Serves the organization logo from blob storage via the API.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOrganizationLogo()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOrganizationLogo$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetOrganizationLogoPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Serves the organization logo from blob storage via the API.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOrganizationLogo$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOrganizationLogo(params: {
    id: number;
  },
  context?: HttpContext

): Observable<void> {

    return this.getOrganizationLogo$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation uploadOrganizationLogo
   */
  static readonly UploadOrganizationLogoPath = '/api/v1/Organizations/{id}/Logo';

  /**
   * Streams an organization logo directly into blob storage.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadOrganizationLogo()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadOrganizationLogo$Response(params: {
    id: number;
    body?: {
'file'?: Blob;
}
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UploadOrganizationLogoPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Streams an organization logo directly into blob storage.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadOrganizationLogo$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadOrganizationLogo(params: {
    id: number;
    body?: {
'file'?: Blob;
}
  },
  context?: HttpContext

): Observable<void> {

    return this.uploadOrganizationLogo$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateCommissionForConsumers
   */
  static readonly UpdateCommissionForConsumersPath = '/api/v1/Organizations/{id}/CommissionForConsumers';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCommissionForConsumers$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCommissionForConsumers$Plain$Response(params: {
    id: number;
    body?: Commission
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Organization>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateCommissionForConsumersPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Organization>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCommissionForConsumers$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCommissionForConsumers$Plain(params: {
    id: number;
    body?: Commission
  },
  context?: HttpContext

): Observable<Organization> {

    return this.updateCommissionForConsumers$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Organization>) => r.body as Organization)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCommissionForConsumers()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCommissionForConsumers$Response(params: {
    id: number;
    body?: Commission
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Organization>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateCommissionForConsumersPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Organization>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCommissionForConsumers$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCommissionForConsumers(params: {
    id: number;
    body?: Commission
  },
  context?: HttpContext

): Observable<Organization> {

    return this.updateCommissionForConsumers$Response(params,context).pipe(
      map((r: StrictHttpResponse<Organization>) => r.body as Organization)
    );
  }

  /**
   * Path part for operation updateCommissionForProfessionals
   */
  static readonly UpdateCommissionForProfessionalsPath = '/api/v1/Organizations/{id}/CommissionForProfessionals';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCommissionForProfessionals$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCommissionForProfessionals$Plain$Response(params: {
    id: number;
    body?: Commission
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Organization>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateCommissionForProfessionalsPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Organization>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCommissionForProfessionals$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCommissionForProfessionals$Plain(params: {
    id: number;
    body?: Commission
  },
  context?: HttpContext

): Observable<Organization> {

    return this.updateCommissionForProfessionals$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Organization>) => r.body as Organization)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCommissionForProfessionals()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCommissionForProfessionals$Response(params: {
    id: number;
    body?: Commission
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Organization>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateCommissionForProfessionalsPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Organization>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCommissionForProfessionals$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCommissionForProfessionals(params: {
    id: number;
    body?: Commission
  },
  context?: HttpContext

): Observable<Organization> {

    return this.updateCommissionForProfessionals$Response(params,context).pipe(
      map((r: StrictHttpResponse<Organization>) => r.body as Organization)
    );
  }

  /**
   * Path part for operation getOctopusTokenById
   */
  static readonly GetOctopusTokenByIdPath = '/api/v1/Organizations/{id}/OctopusToken';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOctopusTokenById$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOctopusTokenById$Plain$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<string>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetOctopusTokenByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<string>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOctopusTokenById$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOctopusTokenById$Plain(params: {
    id: number;
  },
  context?: HttpContext

): Observable<string> {

    return this.getOctopusTokenById$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<string>) => r.body as string)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOctopusTokenById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOctopusTokenById$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<string>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetOctopusTokenByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<string>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOctopusTokenById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOctopusTokenById(params: {
    id: number;
  },
  context?: HttpContext

): Observable<string> {

    return this.getOctopusTokenById$Response(params,context).pipe(
      map((r: StrictHttpResponse<string>) => r.body as string)
    );
  }

  /**
   * Path part for operation getCadastralUserSettings
   */
  static readonly GetCadastralUserSettingsPath = '/api/v1/Organizations/{id}/CadastralUserSettings';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCadastralUserSettings$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCadastralUserSettings$Plain$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CadastralUserSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetCadastralUserSettingsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CadastralUserSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCadastralUserSettings$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCadastralUserSettings$Plain(params: {
    id: number;
  },
  context?: HttpContext

): Observable<CadastralUserSettings> {

    return this.getCadastralUserSettings$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<CadastralUserSettings>) => r.body as CadastralUserSettings)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCadastralUserSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCadastralUserSettings$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CadastralUserSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetCadastralUserSettingsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CadastralUserSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCadastralUserSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCadastralUserSettings(params: {
    id: number;
  },
  context?: HttpContext

): Observable<CadastralUserSettings> {

    return this.getCadastralUserSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<CadastralUserSettings>) => r.body as CadastralUserSettings)
    );
  }

  /**
   * Path part for operation updateCadastralUserSettings
   */
  static readonly UpdateCadastralUserSettingsPath = '/api/v1/Organizations/{id}/CadastralUserSettings';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCadastralUserSettings$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCadastralUserSettings$Plain$Response(params: {
    id: number;
    body?: CadastralUserSettings
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CadastralUserSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateCadastralUserSettingsPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CadastralUserSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCadastralUserSettings$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCadastralUserSettings$Plain(params: {
    id: number;
    body?: CadastralUserSettings
  },
  context?: HttpContext

): Observable<CadastralUserSettings> {

    return this.updateCadastralUserSettings$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<CadastralUserSettings>) => r.body as CadastralUserSettings)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCadastralUserSettings()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCadastralUserSettings$Response(params: {
    id: number;
    body?: CadastralUserSettings
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CadastralUserSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateCadastralUserSettingsPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CadastralUserSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCadastralUserSettings$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCadastralUserSettings(params: {
    id: number;
    body?: CadastralUserSettings
  },
  context?: HttpContext

): Observable<CadastralUserSettings> {

    return this.updateCadastralUserSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<CadastralUserSettings>) => r.body as CadastralUserSettings)
    );
  }

  /**
   * Path part for operation getCostHitAllowed
   */
  static readonly GetCostHitAllowedPath = '/api/v1/Organizations/{id}/CostHitAllowed';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCostHitAllowed$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCostHitAllowed$Plain$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CostHitAllowed>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetCostHitAllowedPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CostHitAllowed>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCostHitAllowed$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCostHitAllowed$Plain(params: {
    id: number;
  },
  context?: HttpContext

): Observable<CostHitAllowed> {

    return this.getCostHitAllowed$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<CostHitAllowed>) => r.body as CostHitAllowed)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCostHitAllowed()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCostHitAllowed$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CostHitAllowed>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetCostHitAllowedPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CostHitAllowed>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCostHitAllowed$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCostHitAllowed(params: {
    id: number;
  },
  context?: HttpContext

): Observable<CostHitAllowed> {

    return this.getCostHitAllowed$Response(params,context).pipe(
      map((r: StrictHttpResponse<CostHitAllowed>) => r.body as CostHitAllowed)
    );
  }

  /**
   * Path part for operation updateCadastralUserAuthorization
   */
  static readonly UpdateCadastralUserAuthorizationPath = '/api/v1/Organizations/{id}/CadastralUserAuthorization';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCadastralUserAuthorization$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCadastralUserAuthorization$Plain$Response(params: {
    id: number;
    body?: CadastralAuthorization
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CadastralUserSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateCadastralUserAuthorizationPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CadastralUserSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCadastralUserAuthorization$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCadastralUserAuthorization$Plain(params: {
    id: number;
    body?: CadastralAuthorization
  },
  context?: HttpContext

): Observable<CadastralUserSettings> {

    return this.updateCadastralUserAuthorization$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<CadastralUserSettings>) => r.body as CadastralUserSettings)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCadastralUserAuthorization()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCadastralUserAuthorization$Response(params: {
    id: number;
    body?: CadastralAuthorization
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CadastralUserSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateCadastralUserAuthorizationPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CadastralUserSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCadastralUserAuthorization$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateCadastralUserAuthorization(params: {
    id: number;
    body?: CadastralAuthorization
  },
  context?: HttpContext

): Observable<CadastralUserSettings> {

    return this.updateCadastralUserAuthorization$Response(params,context).pipe(
      map((r: StrictHttpResponse<CadastralUserSettings>) => r.body as CadastralUserSettings)
    );
  }

  /**
   * Path part for operation getRateSettings
   */
  static readonly GetRateSettingsPath = '/api/v1/Organizations/{id}/RateSettings';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateSettings$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateSettings$Plain$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateSettings>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateSettingsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateSettings>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateSettings$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateSettings$Plain(params: {
    id: number;
  },
  context?: HttpContext

): Observable<Array<RateSettings>> {

    return this.getRateSettings$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateSettings>>) => r.body as Array<RateSettings>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateSettings$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateSettings>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateSettingsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateSettings>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateSettings(params: {
    id: number;
  },
  context?: HttpContext

): Observable<Array<RateSettings>> {

    return this.getRateSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateSettings>>) => r.body as Array<RateSettings>)
    );
  }

  /**
   * Path part for operation updateRateSettings
   */
  static readonly UpdateRateSettingsPath = '/api/v1/Organizations/{id}/RateSettings';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRateSettings$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateSettings$Plain$Response(params: {
    id: number;
    body?: RateSettings
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateSettings>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateRateSettingsPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateSettings>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRateSettings$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateSettings$Plain(params: {
    id: number;
    body?: RateSettings
  },
  context?: HttpContext

): Observable<Array<RateSettings>> {

    return this.updateRateSettings$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateSettings>>) => r.body as Array<RateSettings>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRateSettings()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateSettings$Response(params: {
    id: number;
    body?: RateSettings
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateSettings>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateRateSettingsPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateSettings>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRateSettings$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateSettings(params: {
    id: number;
    body?: RateSettings
  },
  context?: HttpContext

): Observable<Array<RateSettings>> {

    return this.updateRateSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateSettings>>) => r.body as Array<RateSettings>)
    );
  }

  /**
   * Path part for operation getRateVersions
   */
  static readonly GetRateVersionsPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateVersions$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateVersions$Plain$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateVersion>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateVersionsPath, 'get');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateVersion>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateVersions$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateVersions$Plain(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<Array<RateVersion>> {

    return this.getRateVersions$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateVersion>>) => r.body as Array<RateVersion>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateVersions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateVersions$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateVersion>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateVersionsPath, 'get');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateVersion>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateVersions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateVersions(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<Array<RateVersion>> {

    return this.getRateVersions$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateVersion>>) => r.body as Array<RateVersion>)
    );
  }

  /**
   * Path part for operation getRateProfile
   */
  static readonly GetRateProfilePath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateProfile$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateProfile$Plain$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateProfile>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateProfilePath, 'get');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateProfile>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateProfile$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateProfile$Plain(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<RateProfile> {

    return this.getRateProfile$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateProfile>) => r.body as RateProfile)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateProfile()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateProfile$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateProfile>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateProfilePath, 'get');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateProfile>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateProfile$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateProfile(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<RateProfile> {

    return this.getRateProfile$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateProfile>) => r.body as RateProfile)
    );
  }

  /**
   * Path part for operation createRateVersion
   */
  static readonly CreateRateVersionPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createRateVersion$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createRateVersion$Plain$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewRateVersion
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateVersion>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.CreateRateVersionPath, 'post');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateVersion>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createRateVersion$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createRateVersion$Plain(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewRateVersion
  },
  context?: HttpContext

): Observable<RateVersion> {

    return this.createRateVersion$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateVersion>) => r.body as RateVersion)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createRateVersion()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createRateVersion$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewRateVersion
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateVersion>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.CreateRateVersionPath, 'post');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateVersion>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createRateVersion$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createRateVersion(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewRateVersion
  },
  context?: HttpContext

): Observable<RateVersion> {

    return this.createRateVersion$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateVersion>) => r.body as RateVersion)
    );
  }

  /**
   * Path part for operation updateRateProfile
   */
  static readonly UpdateRateProfilePath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRateProfile$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateProfile$Plain$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: RateProfile
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateProfile>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateRateProfilePath, 'put');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateProfile>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRateProfile$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateProfile$Plain(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: RateProfile
  },
  context?: HttpContext

): Observable<RateProfile> {

    return this.updateRateProfile$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateProfile>) => r.body as RateProfile)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRateProfile()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateProfile$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: RateProfile
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateProfile>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateRateProfilePath, 'put');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateProfile>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRateProfile$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateProfile(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: RateProfile
  },
  context?: HttpContext

): Observable<RateProfile> {

    return this.updateRateProfile$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateProfile>) => r.body as RateProfile)
    );
  }

  /**
   * Path part for operation updateRateLtvClasses
   */
  static readonly UpdateRateLtvClassesPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}/LtvClasses';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRateLtvClasses$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateLtvClasses$Plain$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RateProfileLtvClass>
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateProfile>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateRateLtvClassesPath, 'put');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateProfile>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRateLtvClasses$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateLtvClasses$Plain(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RateProfileLtvClass>
  },
  context?: HttpContext

): Observable<RateProfile> {

    return this.updateRateLtvClasses$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateProfile>) => r.body as RateProfile)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRateLtvClasses()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateLtvClasses$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RateProfileLtvClass>
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateProfile>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateRateLtvClassesPath, 'put');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateProfile>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRateLtvClasses$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateLtvClasses(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RateProfileLtvClass>
  },
  context?: HttpContext

): Observable<RateProfile> {

    return this.updateRateLtvClasses$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateProfile>) => r.body as RateProfile)
    );
  }

  /**
   * Path part for operation getRateInterestSheets
   */
  static readonly GetRateInterestSheetsPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}/InterestSheets';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateInterestSheets$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateInterestSheets$Plain$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateInterestSheet>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateInterestSheetsPath, 'get');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateInterestSheet>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateInterestSheets$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateInterestSheets$Plain(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<Array<RateInterestSheet>> {

    return this.getRateInterestSheets$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateInterestSheet>>) => r.body as Array<RateInterestSheet>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateInterestSheets()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateInterestSheets$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateInterestSheet>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateInterestSheetsPath, 'get');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateInterestSheet>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateInterestSheets$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateInterestSheets(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<Array<RateInterestSheet>> {

    return this.getRateInterestSheets$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateInterestSheet>>) => r.body as Array<RateInterestSheet>)
    );
  }

  /**
   * Path part for operation createRateInterestSheet
   */
  static readonly CreateRateInterestSheetPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}/InterestSheets';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createRateInterestSheet$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createRateInterestSheet$Plain$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewInterestSheet
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateInterestSheet>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.CreateRateInterestSheetPath, 'post');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateInterestSheet>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createRateInterestSheet$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createRateInterestSheet$Plain(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewInterestSheet
  },
  context?: HttpContext

): Observable<RateInterestSheet> {

    return this.createRateInterestSheet$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateInterestSheet>) => r.body as RateInterestSheet)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createRateInterestSheet()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createRateInterestSheet$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewInterestSheet
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateInterestSheet>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.CreateRateInterestSheetPath, 'post');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateInterestSheet>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createRateInterestSheet$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createRateInterestSheet(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewInterestSheet
  },
  context?: HttpContext

): Observable<RateInterestSheet> {

    return this.createRateInterestSheet$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateInterestSheet>) => r.body as RateInterestSheet)
    );
  }

  /**
   * Path part for operation getRateInterestSheet
   */
  static readonly GetRateInterestSheetPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}/InterestSheets/{sheetId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateInterestSheet$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateInterestSheet$Plain$Response(params: {
    sheetId: string;
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateInterestSheetProfile>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateInterestSheetPath, 'get');
    if (params) {
      rb.path('sheetId', params.sheetId, {});
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateInterestSheetProfile>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateInterestSheet$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateInterestSheet$Plain(params: {
    sheetId: string;
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<RateInterestSheetProfile> {

    return this.getRateInterestSheet$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateInterestSheetProfile>) => r.body as RateInterestSheetProfile)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateInterestSheet()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateInterestSheet$Response(params: {
    sheetId: string;
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateInterestSheetProfile>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateInterestSheetPath, 'get');
    if (params) {
      rb.path('sheetId', params.sheetId, {});
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateInterestSheetProfile>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateInterestSheet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateInterestSheet(params: {
    sheetId: string;
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<RateInterestSheetProfile> {

    return this.getRateInterestSheet$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateInterestSheetProfile>) => r.body as RateInterestSheetProfile)
    );
  }

  /**
   * Path part for operation getRatePostcodes
   */
  static readonly GetRatePostcodesPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}/Postcodes';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRatePostcodes$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRatePostcodes$Plain$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RatePostcode>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRatePostcodesPath, 'get');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RatePostcode>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRatePostcodes$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRatePostcodes$Plain(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<Array<RatePostcode>> {

    return this.getRatePostcodes$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RatePostcode>>) => r.body as Array<RatePostcode>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRatePostcodes()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRatePostcodes$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RatePostcode>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRatePostcodesPath, 'get');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RatePostcode>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRatePostcodes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRatePostcodes(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<Array<RatePostcode>> {

    return this.getRatePostcodes$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RatePostcode>>) => r.body as Array<RatePostcode>)
    );
  }

  /**
   * Path part for operation updateRatePostcodes
   */
  static readonly UpdateRatePostcodesPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}/Postcodes';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRatePostcodes$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRatePostcodes$Plain$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RatePostcode>
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RatePostcode>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateRatePostcodesPath, 'put');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RatePostcode>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRatePostcodes$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRatePostcodes$Plain(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RatePostcode>
  },
  context?: HttpContext

): Observable<Array<RatePostcode>> {

    return this.updateRatePostcodes$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RatePostcode>>) => r.body as Array<RatePostcode>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRatePostcodes()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRatePostcodes$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RatePostcode>
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RatePostcode>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateRatePostcodesPath, 'put');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RatePostcode>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRatePostcodes$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRatePostcodes(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RatePostcode>
  },
  context?: HttpContext

): Observable<Array<RatePostcode>> {

    return this.updateRatePostcodes$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RatePostcode>>) => r.body as Array<RatePostcode>)
    );
  }

  /**
   * Path part for operation getRateRegions
   */
  static readonly GetRateRegionsPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}/Regions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateRegions$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateRegions$Plain$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateRegionList>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateRegionsPath, 'get');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateRegionList>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateRegions$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateRegions$Plain(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<Array<RateRegionList>> {

    return this.getRateRegions$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateRegionList>>) => r.body as Array<RateRegionList>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateRegions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateRegions$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateRegionList>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateRegionsPath, 'get');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateRegionList>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateRegions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateRegions(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<Array<RateRegionList>> {

    return this.getRateRegions$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateRegionList>>) => r.body as Array<RateRegionList>)
    );
  }

  /**
   * Path part for operation updateRateRegions
   */
  static readonly UpdateRateRegionsPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}/Regions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRateRegions$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateRegions$Plain$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RateRegionList>
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateRegionList>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateRateRegionsPath, 'put');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateRegionList>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRateRegions$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateRegions$Plain(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RateRegionList>
  },
  context?: HttpContext

): Observable<Array<RateRegionList>> {

    return this.updateRateRegions$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateRegionList>>) => r.body as Array<RateRegionList>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRateRegions()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateRegions$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RateRegionList>
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateRegionList>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateRateRegionsPath, 'put');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateRegionList>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRateRegions$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRateRegions(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: Array<RateRegionList>
  },
  context?: HttpContext

): Observable<Array<RateRegionList>> {

    return this.updateRateRegions$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateRegionList>>) => r.body as Array<RateRegionList>)
    );
  }

  /**
   * Path part for operation checkRateVersion
   */
  static readonly CheckRateVersionPath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Versions/{versionId}/Check';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `checkRateVersion$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  checkRateVersion$Plain$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateConfigurationIssue>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.CheckRateVersionPath, 'get');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateConfigurationIssue>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `checkRateVersion$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  checkRateVersion$Plain(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<Array<RateConfigurationIssue>> {

    return this.checkRateVersion$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateConfigurationIssue>>) => r.body as Array<RateConfigurationIssue>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `checkRateVersion()` instead.
   *
   * This method doesn't expect any request body.
   */
  checkRateVersion$Response(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RateConfigurationIssue>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.CheckRateVersionPath, 'get');
    if (params) {
      rb.path('versionId', params.versionId, {});
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RateConfigurationIssue>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `checkRateVersion$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  checkRateVersion(params: {
    versionId: string;
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<Array<RateConfigurationIssue>> {

    return this.checkRateVersion$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RateConfigurationIssue>>) => r.body as Array<RateConfigurationIssue>)
    );
  }

  /**
   * Path part for operation getRateProfilePermissions
   */
  static readonly GetRateProfilePermissionsPath = '/api/v1/Organizations/{id}/RateSettings/Permissions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateProfilePermissions$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateProfilePermissions$Plain$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateProfilePermissions>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateProfilePermissionsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateProfilePermissions>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateProfilePermissions$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateProfilePermissions$Plain(params: {
    id: number;
  },
  context?: HttpContext

): Observable<RateProfilePermissions> {

    return this.getRateProfilePermissions$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateProfilePermissions>) => r.body as RateProfilePermissions)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRateProfilePermissions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateProfilePermissions$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateProfilePermissions>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetRateProfilePermissionsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateProfilePermissions>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRateProfilePermissions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRateProfilePermissions(params: {
    id: number;
  },
  context?: HttpContext

): Observable<RateProfilePermissions> {

    return this.getRateProfilePermissions$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateProfilePermissions>) => r.body as RateProfilePermissions)
    );
  }

  /**
   * Path part for operation createLenderRate
   */
  static readonly CreateLenderRatePath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createLenderRate$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createLenderRate$Plain$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewLenderRate
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateVersion>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.CreateLenderRatePath, 'post');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateVersion>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createLenderRate$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createLenderRate$Plain(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewLenderRate
  },
  context?: HttpContext

): Observable<RateVersion> {

    return this.createLenderRate$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateVersion>) => r.body as RateVersion)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createLenderRate()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createLenderRate$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewLenderRate
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateVersion>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.CreateLenderRatePath, 'post');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateVersion>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createLenderRate$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createLenderRate(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewLenderRate
  },
  context?: HttpContext

): Observable<RateVersion> {

    return this.createLenderRate$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateVersion>) => r.body as RateVersion)
    );
  }

  /**
   * Path part for operation discontinueLenderRate
   */
  static readonly DiscontinueLenderRatePath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Discontinue';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `discontinueLenderRate$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  discontinueLenderRate$Plain$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: DiscontinueLenderRate
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateVersion>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.DiscontinueLenderRatePath, 'post');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateVersion>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `discontinueLenderRate$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  discontinueLenderRate$Plain(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: DiscontinueLenderRate
  },
  context?: HttpContext

): Observable<RateVersion> {

    return this.discontinueLenderRate$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateVersion>) => r.body as RateVersion)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `discontinueLenderRate()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  discontinueLenderRate$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: DiscontinueLenderRate
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateVersion>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.DiscontinueLenderRatePath, 'post');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateVersion>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `discontinueLenderRate$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  discontinueLenderRate(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: DiscontinueLenderRate
  },
  context?: HttpContext

): Observable<RateVersion> {

    return this.discontinueLenderRate$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateVersion>) => r.body as RateVersion)
    );
  }

  /**
   * Path part for operation resumeLenderRate
   */
  static readonly ResumeLenderRatePath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}/Resume';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `resumeLenderRate$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  resumeLenderRate$Plain$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewRateVersion
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateVersion>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.ResumeLenderRatePath, 'post');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateVersion>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `resumeLenderRate$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  resumeLenderRate$Plain(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewRateVersion
  },
  context?: HttpContext

): Observable<RateVersion> {

    return this.resumeLenderRate$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateVersion>) => r.body as RateVersion)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `resumeLenderRate()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  resumeLenderRate$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewRateVersion
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateVersion>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.ResumeLenderRatePath, 'post');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateVersion>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `resumeLenderRate$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  resumeLenderRate(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
    body?: NewRateVersion
  },
  context?: HttpContext

): Observable<RateVersion> {

    return this.resumeLenderRate$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateVersion>) => r.body as RateVersion)
    );
  }

  /**
   * Path part for operation exportRateSettings
   */
  static readonly ExportRateSettingsPath = '/api/v1/Organizations/{id}/RateSettings/Export';

  /**
   * Exports every version of every rate of the lenders (all lenders when none are given): the profile, the location lists and
   * every interest sheet. Admins only; an admin only gets the rates of the organization.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportRateSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportRateSettings$Response(params: {
    lenderNumbers?: Array<number>;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateExport>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.ExportRateSettingsPath, 'get');
    if (params) {
      rb.query('lenderNumbers', params.lenderNumbers, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateExport>;
      })
    );
  }

  /**
   * Exports every version of every rate of the lenders (all lenders when none are given): the profile, the location lists and
   * every interest sheet. Admins only; an admin only gets the rates of the organization.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportRateSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportRateSettings(params: {
    lenderNumbers?: Array<number>;
    id: number;
  },
  context?: HttpContext

): Observable<RateExport> {

    return this.exportRateSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateExport>) => r.body as RateExport)
    );
  }

  /**
   * Path part for operation importRateSettings
   */
  static readonly ImportRateSettingsPath = '/api/v1/Organizations/{id}/RateSettings/Import';

  /**
   * Imports an export: overwrites the open versions that match a version in it on lender, rate number and validity (valid from and to).
   * Closed and unknown versions are reported and left alone. With dryRun nothing is changed, only reported (super admins only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `importRateSettings()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  importRateSettings$Response(params: {
    id: number;
    dryRun?: boolean;
    body?: RateExport
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RateImportResult>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.ImportRateSettingsPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('dryRun', params.dryRun, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RateImportResult>;
      })
    );
  }

  /**
   * Imports an export: overwrites the open versions that match a version in it on lender, rate number and validity (valid from and to).
   * Closed and unknown versions are reported and left alone. With dryRun nothing is changed, only reported (super admins only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `importRateSettings$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  importRateSettings(params: {
    id: number;
    dryRun?: boolean;
    body?: RateExport
  },
  context?: HttpContext

): Observable<RateImportResult> {

    return this.importRateSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<RateImportResult>) => r.body as RateImportResult)
    );
  }

  /**
   * Path part for operation getLenderOverview
   */
  static readonly GetLenderOverviewPath = '/api/v1/Organizations/{id}/RateSettings/Lenders';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getLenderOverview$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getLenderOverview$Plain$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<LenderOverview>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetLenderOverviewPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<LenderOverview>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getLenderOverview$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getLenderOverview$Plain(params: {
    id: number;
  },
  context?: HttpContext

): Observable<Array<LenderOverview>> {

    return this.getLenderOverview$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<LenderOverview>>) => r.body as Array<LenderOverview>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getLenderOverview()` instead.
   *
   * This method doesn't expect any request body.
   */
  getLenderOverview$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<LenderOverview>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetLenderOverviewPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<LenderOverview>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getLenderOverview$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getLenderOverview(params: {
    id: number;
  },
  context?: HttpContext

): Observable<Array<LenderOverview>> {

    return this.getLenderOverview$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<LenderOverview>>) => r.body as Array<LenderOverview>)
    );
  }

  /**
   * Path part for operation createLender
   */
  static readonly CreateLenderPath = '/api/v1/Organizations/{id}/RateSettings/Lenders';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createLender$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createLender$Plain$Response(params: {
    id: number;
    body?: NewLender
  },
  context?: HttpContext

): Observable<StrictHttpResponse<LenderOverview>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.CreateLenderPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LenderOverview>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createLender$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createLender$Plain(params: {
    id: number;
    body?: NewLender
  },
  context?: HttpContext

): Observable<LenderOverview> {

    return this.createLender$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<LenderOverview>) => r.body as LenderOverview)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createLender()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createLender$Response(params: {
    id: number;
    body?: NewLender
  },
  context?: HttpContext

): Observable<StrictHttpResponse<LenderOverview>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.CreateLenderPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LenderOverview>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createLender$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createLender(params: {
    id: number;
    body?: NewLender
  },
  context?: HttpContext

): Observable<LenderOverview> {

    return this.createLender$Response(params,context).pipe(
      map((r: StrictHttpResponse<LenderOverview>) => r.body as LenderOverview)
    );
  }

  /**
   * Path part for operation updateLenderSettings
   */
  static readonly UpdateLenderSettingsPath = '/api/v1/Organizations/{id}/RateSettings/Lenders/{number}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateLenderSettings$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateLenderSettings$Plain$Response(params: {
    number: number;
    id: number;
    body?: LenderChange
  },
  context?: HttpContext

): Observable<StrictHttpResponse<LenderOverview>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateLenderSettingsPath, 'put');
    if (params) {
      rb.path('number', params.number, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LenderOverview>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateLenderSettings$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateLenderSettings$Plain(params: {
    number: number;
    id: number;
    body?: LenderChange
  },
  context?: HttpContext

): Observable<LenderOverview> {

    return this.updateLenderSettings$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<LenderOverview>) => r.body as LenderOverview)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateLenderSettings()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateLenderSettings$Response(params: {
    number: number;
    id: number;
    body?: LenderChange
  },
  context?: HttpContext

): Observable<StrictHttpResponse<LenderOverview>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateLenderSettingsPath, 'put');
    if (params) {
      rb.path('number', params.number, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LenderOverview>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateLenderSettings$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateLenderSettings(params: {
    number: number;
    id: number;
    body?: LenderChange
  },
  context?: HttpContext

): Observable<LenderOverview> {

    return this.updateLenderSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<LenderOverview>) => r.body as LenderOverview)
    );
  }

  /**
   * Path part for operation deleteLenderSettings
   */
  static readonly DeleteLenderSettingsPath = '/api/v1/Organizations/{id}/RateSettings/Lenders/{number}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteLenderSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteLenderSettings$Response(params: {
    number: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.DeleteLenderSettingsPath, 'delete');
    if (params) {
      rb.path('number', params.number, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteLenderSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteLenderSettings(params: {
    number: number;
    id: number;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteLenderSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteLenderRate
   */
  static readonly DeleteLenderRatePath = '/api/v1/Organizations/{id}/RateSettings/{lenderNumber}/{rateNumber}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteLenderRate()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteLenderRate$Response(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.DeleteLenderRatePath, 'delete');
    if (params) {
      rb.path('rateNumber', params.rateNumber, {});
      rb.path('lenderNumber', params.lenderNumber, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteLenderRate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteLenderRate(params: {
    rateNumber: number;
    lenderNumber: number;
    id: number;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteLenderRate$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getUsers
   */
  static readonly GetUsersPath = '/api/v1/Organizations/{id}/User';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUsers$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUsers$Plain$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<User>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetUsersPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<User>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUsers$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUsers$Plain(params: {
    id: number;
  },
  context?: HttpContext

): Observable<Array<User>> {

    return this.getUsers$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<User>>) => r.body as Array<User>)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUsers()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUsers$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<User>>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetUsersPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<User>>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUsers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUsers(params: {
    id: number;
  },
  context?: HttpContext

): Observable<Array<User>> {

    return this.getUsers$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<User>>) => r.body as Array<User>)
    );
  }

  /**
   * Path part for operation getUserById
   */
  static readonly GetUserByIdPath = '/api/v1/Organizations/{id}/Users/{userId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUserById$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserById$Plain$Response(params: {
    id: number;
    userId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<User>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetUserByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.path('userId', params.userId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<User>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUserById$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserById$Plain(params: {
    id: number;
    userId: string;
  },
  context?: HttpContext

): Observable<User> {

    return this.getUserById$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<User>) => r.body as User)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUserById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserById$Response(params: {
    id: number;
    userId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<User>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetUserByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.path('userId', params.userId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<User>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUserById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserById(params: {
    id: number;
    userId: string;
  },
  context?: HttpContext

): Observable<User> {

    return this.getUserById$Response(params,context).pipe(
      map((r: StrictHttpResponse<User>) => r.body as User)
    );
  }

  /**
   * Path part for operation updateUser
   */
  static readonly UpdateUserPath = '/api/v1/Organizations/{id}/Users/{userId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateUser$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateUser$Plain$Response(params: {
    id: number;
    userId: string;
    body?: User
  },
  context?: HttpContext

): Observable<StrictHttpResponse<User>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateUserPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.path('userId', params.userId, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<User>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateUser$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateUser$Plain(params: {
    id: number;
    userId: string;
    body?: User
  },
  context?: HttpContext

): Observable<User> {

    return this.updateUser$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<User>) => r.body as User)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateUser()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateUser$Response(params: {
    id: number;
    userId: string;
    body?: User
  },
  context?: HttpContext

): Observable<StrictHttpResponse<User>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateUserPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.path('userId', params.userId, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<User>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateUser$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateUser(params: {
    id: number;
    userId: string;
    body?: User
  },
  context?: HttpContext

): Observable<User> {

    return this.updateUser$Response(params,context).pipe(
      map((r: StrictHttpResponse<User>) => r.body as User)
    );
  }

  /**
   * Path part for operation deleteUserById
   */
  static readonly DeleteUserByIdPath = '/api/v1/Organizations/{id}/Users/{userId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteUserById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUserById$Response(params: {
    id: number;
    userId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.DeleteUserByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
      rb.path('userId', params.userId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteUserById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUserById(params: {
    id: number;
    userId: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteUserById$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation addUser
   */
  static readonly AddUserPath = '/api/v1/Organizations/{id}/Users';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addUser$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  addUser$Plain$Response(params: {
    id: number;
    body?: User
  },
  context?: HttpContext

): Observable<StrictHttpResponse<User>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.AddUserPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<User>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addUser$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  addUser$Plain(params: {
    id: number;
    body?: User
  },
  context?: HttpContext

): Observable<User> {

    return this.addUser$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<User>) => r.body as User)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addUser()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  addUser$Response(params: {
    id: number;
    body?: User
  },
  context?: HttpContext

): Observable<StrictHttpResponse<User>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.AddUserPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<User>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addUser$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  addUser(params: {
    id: number;
    body?: User
  },
  context?: HttpContext

): Observable<User> {

    return this.addUser$Response(params,context).pipe(
      map((r: StrictHttpResponse<User>) => r.body as User)
    );
  }

  /**
   * Path part for operation sendOrganizationPasswordResetEmail
   */
  static readonly SendOrganizationPasswordResetEmailPath = '/api/v1/Organizations/{id}/Users/{userId}/SendPasswordResetEmail';

  /**
   * Sends out a password reset email to the user.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `sendOrganizationPasswordResetEmail()` instead.
   *
   * This method doesn't expect any request body.
   */
  sendOrganizationPasswordResetEmail$Response(params: {

    /**
     * The id of the organization
     */
    id: number;

    /**
     * The id of the user
     */
    userId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.SendOrganizationPasswordResetEmailPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.path('userId', params.userId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Sends out a password reset email to the user.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `sendOrganizationPasswordResetEmail$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  sendOrganizationPasswordResetEmail(params: {

    /**
     * The id of the organization
     */
    id: number;

    /**
     * The id of the user
     */
    userId: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.sendOrganizationPasswordResetEmail$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getDocumentSyncSettings
   */
  static readonly GetDocumentSyncSettingsPath = '/api/v1/Organizations/{id}/DocumentSyncSettings';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDocumentSyncSettings$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocumentSyncSettings$Plain$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentSyncSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetDocumentSyncSettingsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentSyncSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDocumentSyncSettings$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocumentSyncSettings$Plain(params: {
    id: number;
  },
  context?: HttpContext

): Observable<DocumentSyncSettings> {

    return this.getDocumentSyncSettings$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentSyncSettings>) => r.body as DocumentSyncSettings)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDocumentSyncSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocumentSyncSettings$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentSyncSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.GetDocumentSyncSettingsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentSyncSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDocumentSyncSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocumentSyncSettings(params: {
    id: number;
  },
  context?: HttpContext

): Observable<DocumentSyncSettings> {

    return this.getDocumentSyncSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentSyncSettings>) => r.body as DocumentSyncSettings)
    );
  }

  /**
   * Path part for operation updateDocumentSyncSettings
   */
  static readonly UpdateDocumentSyncSettingsPath = '/api/v1/Organizations/{id}/DocumentSyncSettings';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateDocumentSyncSettings$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateDocumentSyncSettings$Plain$Response(params: {
    id: number;
    body?: DocumentSyncSettings
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentSyncSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateDocumentSyncSettingsPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentSyncSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateDocumentSyncSettings$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateDocumentSyncSettings$Plain(params: {
    id: number;
    body?: DocumentSyncSettings
  },
  context?: HttpContext

): Observable<DocumentSyncSettings> {

    return this.updateDocumentSyncSettings$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentSyncSettings>) => r.body as DocumentSyncSettings)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateDocumentSyncSettings()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateDocumentSyncSettings$Response(params: {
    id: number;
    body?: DocumentSyncSettings
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentSyncSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.UpdateDocumentSyncSettingsPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentSyncSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateDocumentSyncSettings$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateDocumentSyncSettings(params: {
    id: number;
    body?: DocumentSyncSettings
  },
  context?: HttpContext

): Observable<DocumentSyncSettings> {

    return this.updateDocumentSyncSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentSyncSettings>) => r.body as DocumentSyncSettings)
    );
  }

  /**
   * Path part for operation testDocumentSyncSettings
   */
  static readonly TestDocumentSyncSettingsPath = '/api/v1/Organizations/{id}/DocumentSyncSettings/Test';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `testDocumentSyncSettings$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  testDocumentSyncSettings$Plain$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentSyncSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.TestDocumentSyncSettingsPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentSyncSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `testDocumentSyncSettings$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  testDocumentSyncSettings$Plain(params: {
    id: number;
  },
  context?: HttpContext

): Observable<DocumentSyncSettings> {

    return this.testDocumentSyncSettings$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentSyncSettings>) => r.body as DocumentSyncSettings)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `testDocumentSyncSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  testDocumentSyncSettings$Response(params: {
    id: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentSyncSettings>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.TestDocumentSyncSettingsPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentSyncSettings>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `testDocumentSyncSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  testDocumentSyncSettings(params: {
    id: number;
  },
  context?: HttpContext

): Observable<DocumentSyncSettings> {

    return this.testDocumentSyncSettings$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentSyncSettings>) => r.body as DocumentSyncSettings)
    );
  }

  /**
   * Path part for operation runDocumentSync
   */
  static readonly RunDocumentSyncPath = '/api/v1/Organizations/{id}/DocumentSyncSettings/Sync';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `runDocumentSync$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  runDocumentSync$Plain$Response(params: {
    id: number;
    limit?: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentSyncRunResult>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.RunDocumentSyncPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('limit', params.limit, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentSyncRunResult>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `runDocumentSync$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  runDocumentSync$Plain(params: {
    id: number;
    limit?: number;
  },
  context?: HttpContext

): Observable<DocumentSyncRunResult> {

    return this.runDocumentSync$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentSyncRunResult>) => r.body as DocumentSyncRunResult)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `runDocumentSync()` instead.
   *
   * This method doesn't expect any request body.
   */
  runDocumentSync$Response(params: {
    id: number;
    limit?: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentSyncRunResult>> {

    const rb = new RequestBuilder(this.rootUrl, OrganizationsService.RunDocumentSyncPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('limit', params.limit, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentSyncRunResult>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `runDocumentSync$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  runDocumentSync(params: {
    id: number;
    limit?: number;
  },
  context?: HttpContext

): Observable<DocumentSyncRunResult> {

    return this.runDocumentSync$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentSyncRunResult>) => r.body as DocumentSyncRunResult)
    );
  }

}
